import { uploadFileApi } from "@/api/services/global/file";
import { Button } from "@/components/elements/Button";
import { FileItem } from "@/components/elements/FileItem";
import { useFormField } from "@/components/elements/FormField";
import { FILE_UPLOAD_FOR, MIME_TYPES } from "@/constants/file";
import { INVALID_PASSWORD_PROTECTED_PDF, INVALID_PDF_FORMAT } from "@/constants/invalids";
import { TRenewVisaUploadFileForm } from "@/features/renew-visa/types/renewVisaUploadFileForm";
import { state } from "@/theme/colors";
import { CHECK_PDF_RESULT } from "@/types/enum";
import { showError } from "@/utils/error";
import { checkFileListWhetherIsActualPdf, convertByteToMb } from "@/utils/file";
import { makeUuid } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { Stack, styled } from "@mui/material";
import { Dispatch, FC, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";

type TFileBoxInputProps = {
  readonly?: boolean;
  multiple?: boolean;
  maxFiles?: number;
  maxSizeMb?: number;
  triggerArrKeys?: (keyof TRenewVisaUploadFileForm)[] | undefined;
  disabled?: boolean;
  setIsUploadingFileKey: Dispatch<SetStateAction<string | undefined>>;
};

export const FileBoxInput: FC<TFileBoxInputProps> = ({
  readonly,
  multiple = false,
  maxSizeMb = 2,
  maxFiles,
  triggerArrKeys,
  setIsUploadingFileKey,
  disabled,
}) => {
  const { field, fieldState } = useFormField();

  const methods = useFormContext<TRenewVisaUploadFileForm>();

  const handleUploadFile = async (files: File[]) => {
    if (files.some((file) => file.size === 0)) {
      toast.error("0Byteのファイルはアップロードできません。");
      return;
    }

    const result = await checkFileListWhetherIsActualPdf(files);
    if (result === CHECK_PDF_RESULT.PASSWORD_PROTECTED) {
      toast.error(INVALID_PASSWORD_PROTECTED_PDF);
      return;
    }
    if (result === CHECK_PDF_RESULT.INVALID) {
      toast.error(INVALID_PDF_FORMAT);
      return;
    }

    try {
      setIsUploadingFileKey(field?.name);
      if (multiple) {
        if (maxFiles && files.length + (field?.value?.length ?? 0) > maxFiles) {
          toast.error(`${maxFiles}ファイル以下を選択してください。`);
          return;
        }

        for (const file of files) {
          if (maxSizeMb && convertByteToMb(file.size) > maxSizeMb) {
            toast.error(`ファイルサイズは${maxSizeMb}Mb以内でお願いします。`);
            return;
          }
        }

        const arrfilePath = await Promise.all(
          files.map(async (file) => {
            const rs = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.RENEW_VISA_FILES });
            return rs.data.filePath;
          }),
        );
        field?.onChange([...(field?.value ?? []), ...arrfilePath]);
      } else {
        const file = files[0];

        if (maxSizeMb && convertByteToMb(file.size) > maxSizeMb) {
          toast.error(`ファイルサイズは${maxSizeMb}Mb以内でお願いします。`);
          return;
        }

        const rs = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.RENEW_VISA_FILES });
        field?.onChange(rs.data.filePath);
      }

      if (!!triggerArrKeys && triggerArrKeys.length > 0) {
        await methods.trigger(triggerArrKeys);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsUploadingFileKey(undefined);
    }
  };

  return (
    <>
      {multiple ? (
        <Stack alignItems="flex-end" gap={1}>
          <Button
            name={field?.name}
            component="label"
            variant="outline"
            disabled={disabled || readonly}
            sx={{
              minWidth: "192px",
              paddingY: "25px !important",
              borderColor: fieldState?.error ? `${state.error_1}!important` : undefined,
              color: fieldState?.error ? `${state.error_1}!important` : undefined,
            }}
          >
            ファイルを選択する
            <VisuallyHiddenInput
              type="file"
              accept={MIME_TYPES.PDF.join("")}
              onChange={(event) => event.target.files && handleUploadFile(Array.from(event.target.files))}
              onClick={(event) => {
                event.currentTarget.value = "";
              }}
              multiple={multiple}
            />
          </Button>
          <Stack>
            {field?.value &&
              field.value.length > 0 &&
              field.value.map((filePath: string) => (
                <FileItemWrap
                  key={makeUuid()}
                  downloadable
                  enableFileSize
                  filePath={filePath}
                  onRemove={!readonly ? () => field.onChange(field.value.filter((item: string) => item !== filePath)) : undefined}
                />
              ))}
          </Stack>
        </Stack>
      ) : (
        <>
          {field?.value ? (
            <FileItemWrap downloadable enableFileSize filePath={field.value} onRemove={!readonly ? () => field.onChange(null) : undefined} />
          ) : (
            <Button
              name={field?.name}
              component="label"
              variant="outline"
              disabled={disabled || readonly}
              sx={{
                minWidth: "192px",
                paddingY: "25px !important",
                borderColor: fieldState?.error ? `${state.error_1}!important` : undefined,
                color: fieldState?.error ? `${state.error_1}!important` : undefined,
              }}
            >
              ファイルを選択する
              <VisuallyHiddenInput
                type="file"
                accept={MIME_TYPES.PDF.join("")}
                onChange={(event) => event.target.files && handleUploadFile(Array.from(event.target.files))}
                onClick={(event) => {
                  event.currentTarget.value = "";
                }}
                multiple={multiple}
              />
            </Button>
          )}
        </>
      )}
    </>
  );
};

const FileItemWrap = styled(FileItem)`
  .MuiTypography-body1 {
    margin-right: 10px;
  }
`;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
